import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, X } from "lucide-react";

const REBORN_COVER = "https://customer-assets.emergentagent.com/job_wolf-cinematic/artifacts/ixucow66_D15A2D70-A49B-4B34-B2A1-0D7BFAD5A4BA.png";
const WOLF_ALBUM_COVER = "https://customer-assets.emergentagent.com/job_wolf-cinematic/artifacts/pbp5yabt_IMG_2649.jpeg";
const NOTHING_MISSING_COVER = "https://customer-assets.emergentagent.com/job_wolf-cinematic/artifacts/iurenmkw_2A418EAD-D8A5-4F55-88D1-E32B846F8093.png";

const RESURRECTED_VIDEO =
  "https://customer-assets.emergentagent.com/job_wolf-cinematic/artifacts/hin9dczk_%40audio%5BRESURRECTED.wav%5D%28RESURRECTED.wav-c45a05d6d4937bc0df250d433dd8cdb1-https-...-watermark-compressed.mov";

const GLORY_BE_TRAILER =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_wolf-cinematic/artifacts/q7nsjcw8_Glory%20Be%20Trailer.mov";
const GLORY_BE_POSTER = "/godwolf-poster.jpg";

const WATCH_WOLF = [
  {
    id: "just-like-you",
    title: "Just Like You",
    length: "Official Film Clip",
    youtube: "zmae2PbZ6mM",
    poster: "https://i.ytimg.com/vi/zmae2PbZ6mM/maxresdefault.jpg",
  },
  {
    id: "carved-inside-my-mind",
    title: "Carved Inside My Mind",
    length: "Official Music Video",
    youtube: "l0WBbMsb2c8",
    poster: "https://i.ytimg.com/vi/l0WBbMsb2c8/maxresdefault.jpg",
  },
  {
    id: "wolf-film-1",
    title: "Unbreakable",
    length: "4:53",
    src: "https://customer-assets-7cd3h4nn.emergentagent.net/job_wolf-cinematic/artifacts/rj048dqb_video-HuiwDMAV51w0siDojR1L.MP4",
    poster: "/watch-wolf-w1.jpg",
  },
  {
    id: "wolf-film-2",
    title: "Still Waiting On You",
    length: "4:59",
    src: "https://customer-assets-7cd3h4nn.emergentagent.net/job_wolf-cinematic/artifacts/8kgfp5xp_video-0In3jQYHF7JTw39XBfC7.MP4",
    poster: "/watch-wolf-w2.jpg",
  },
];

const videos = [
  {
    id: "resurrected",
    kind: "Music Video",
    title: "Resurrected",
    album: "Reborn",
    src: RESURRECTED_VIDEO,
    poster: REBORN_COVER,
    available: true,
  },
  {
    id: "video-2",
    kind: "Music Video",
    title: "Coming next",
    album: "Reborn",
    src: null,
    poster: WOLF_ALBUM_COVER,
    available: false,
  },
  {
    id: "video-3",
    kind: "Music Video",
    title: "Coming next",
    album: "Reborn",
    src: null,
    poster: NOTHING_MISSING_COVER,
    available: false,
  },
];

function VideoPlayer({ src, poster, autoPlay = false, onClose, onEnded }) {
  const ref = useRef(null);

  useEffect(() => {
    const v = ref.current;
    if (!v || !onEnded) return;
    v.addEventListener("ended", onEnded);
    return () => v.removeEventListener("ended", onEnded);
  }, [onEnded]);

  // Reset & play when src changes (theater mode advance)
  useEffect(() => {
    const v = ref.current;
    if (!v || !src) return;
    v.load();
    if (autoPlay) {
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
  }, [src, autoPlay]);

  return (
    <div className="relative w-full h-full bg-black" data-testid="video-player">
      <video
        ref={ref}
        src={src}
        poster={poster}
        playsInline
        autoPlay={autoPlay}
        muted={autoPlay}
        controls
        preload="metadata"
        className="w-full h-full object-contain"
        data-testid="video-element"
      />
      {onClose && (
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 text-[#D4AF37] hover:text-[#F4C542]"
          data-testid="video-close-btn"
          aria-label="Close"
        >
          <X size={22} />
        </button>
      )}
    </div>
  );
}

export default function Media() {
  const [activeIdx, setActiveIdx] = useState(null);
  const [trailerPlaying, setTrailerPlaying] = useState(false);
  const [watchPlaying, setWatchPlaying] = useState(null);
  const [trilogy, setTrilogy] = useState(null); // null | { idx, mode: 'video' | 'interstitial' | 'end' }
  const featured = videos[0];

  const startTrilogy = () => setTrilogy({ idx: 0, mode: "video" });
  const closeTrilogy = () => setTrilogy(null);

  // Advance: if next is available -> play it. If not -> interstitial then try next.
  const advanceTrilogy = () => {
    setTrilogy((t) => {
      if (!t) return t;
      const next = t.idx + 1;
      if (next >= videos.length) return { idx: t.idx, mode: "end" };
      if (videos[next].available) return { idx: next, mode: "video" };
      return { idx: next, mode: "interstitial" };
    });
  };

  // Auto-skip past coming-soon interstitial after 5s
  useEffect(() => {
    if (!trilogy || trilogy.mode !== "interstitial") return;
    const t = setTimeout(() => advanceTrilogy(), 5000);
    return () => clearTimeout(t);
  }, [trilogy]);

  return (
    <div className="pt-32 sm:pt-40 pb-20 bg-[#080808] min-h-screen" data-testid="media-page">
      <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-16">
        <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] mb-6">— Media</p>
        <h1 className="font-display wolf-gold-foil text-6xl sm:text-8xl lg:text-9xl leading-[0.85]">Visuals</h1>
        <p className="font-editorial italic text-xl sm:text-2xl text-white/55 mt-6 max-w-2xl">
          Music videos, cinematic teasers, the world in motion.
        </p>

        {/* GODWOLF — ALBUM TRAILER */}
        <div className="mt-16 border border-[#D4AF37]/25 bg-[#0d0d0d] overflow-hidden" data-testid="godwolf-trailer-section">
          <div className="grid lg:grid-cols-2">
            <div className="p-8 sm:p-12 lg:p-16 flex flex-col justify-center order-2 lg:order-1">
              <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37]">— Official Trailer · The New Album</p>
              <h2 className="font-display wolf-gold-foil text-6xl sm:text-8xl leading-[0.85] mt-4">Godwolf</h2>
              <p className="font-display text-2xl sm:text-3xl text-white mt-6 tracking-wider">"GLORY BE"</p>
              <p className="font-editorial italic text-lg sm:text-xl text-white/55 mt-4 max-w-md">
                The first glimpse of the next chapter. Walk with the King.
              </p>
              {!trailerPlaying && (
                <button
                  type="button"
                  onClick={() => setTrailerPlaying(true)}
                  data-testid="godwolf-trailer-play"
                  className="mt-8 self-start group flex items-center gap-3 btn-gold-fill text-black font-display tracking-[0.4em] text-sm px-8 py-4 border border-[#D4AF37] transition-all"
                >
                  <Play size={16} />
                  <span>WATCH THE TRAILER</span>
                </button>
              )}
            </div>
            <div className="relative bg-black order-1 lg:order-2 flex items-center justify-center">
              <div className="relative w-full max-w-[420px] aspect-[9/16] mt-14 mb-6 lg:my-12 border border-[#D4AF37]/20">
                {trailerPlaying ? (
                  <VideoPlayer src={GLORY_BE_TRAILER} poster={GLORY_BE_POSTER} autoPlay onClose={() => setTrailerPlaying(false)} />
                ) : (
                  <button
                    type="button"
                    onClick={() => setTrailerPlaying(true)}
                    className="group block w-full h-full text-left"
                    data-testid="godwolf-trailer-poster"
                  >
                    <img
                      src={GLORY_BE_POSTER}
                      alt="Glory Be — Godwolf album trailer"
                      className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-[1200ms]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border border-[#D4AF37]/60 flex items-center justify-center group-hover:border-[#D4AF37] group-hover:bg-[#D4AF37]/10 transition-all backdrop-blur-sm bg-black/40">
                        <Play size={28} className="text-[#D4AF37] ml-1" />
                      </div>
                    </div>
                    <div className="absolute bottom-5 left-5">
                      <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">— Trailer · 1:05</p>
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* WATCH WOLF */}
        <section className="mt-20" data-testid="watch-wolf-section">
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] mb-4">— The Films</p>
          <h2 className="font-display wolf-gold-foil text-4xl sm:text-6xl leading-none">Watch WOLF</h2>
          <div className="mt-10 grid sm:grid-cols-2 gap-6 sm:gap-8">
            {WATCH_WOLF.map((v) => (
              <div key={v.id} className="border border-white/10 hover:border-[#D4AF37]/40 bg-[#0d0d0d] transition-all">
                <div className="relative aspect-video bg-black">
                  {watchPlaying === v.id ? (
                    v.youtube ? (
                      <iframe
                        className="absolute inset-0 w-full h-full"
                        src={`https://www.youtube.com/embed/${v.youtube}?autoplay=1&rel=0`}
                        title={v.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                        data-testid={`watch-wolf-youtube-${v.id}`}
                      />
                    ) : (
                      <VideoPlayer src={v.src} poster={v.poster} autoPlay onClose={() => setWatchPlaying(null)} />
                    )
                  ) : (
                    <button
                      type="button"
                      onClick={() => setWatchPlaying(v.id)}
                      className="group block w-full h-full text-left"
                      data-testid={`watch-wolf-play-${v.id}`}
                    >
                      <img
                        src={v.poster}
                        alt={v.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-[1200ms]"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full border border-[#D4AF37]/60 flex items-center justify-center group-hover:border-[#D4AF37] group-hover:bg-[#D4AF37]/10 transition-all backdrop-blur-sm bg-black/40">
                          <Play size={24} className="text-[#D4AF37] ml-1" />
                        </div>
                      </div>
                      <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between">
                        <p className="font-display text-xl sm:text-2xl text-white">{v.title}</p>
                        <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">{v.length}</p>
                      </div>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FEATURED — RESURRECTED */}
        <div
          className="mt-16 relative aspect-video bg-black border border-[#D4AF37]/25 overflow-hidden"
          data-testid="media-featured"
        >
          {activeIdx === 0 ? (
            <VideoPlayer src={featured.src} poster={featured.poster} autoPlay onClose={() => setActiveIdx(null)} />
          ) : (
            <button
              type="button"
              onClick={() => setActiveIdx(0)}
              className="group block w-full h-full text-left"
              data-testid="media-featured-play"
            >
              <img
                src={featured.poster}
                alt={featured.title}
                className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-85 transition-opacity duration-[1200ms] group-hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/30" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 sm:w-28 sm:h-28 rounded-full border border-[#D4AF37]/60 flex items-center justify-center group-hover:border-[#D4AF37] group-hover:bg-[#D4AF37]/10 transition-all backdrop-blur-sm bg-black/40">
                  <Play size={36} className="text-[#D4AF37] ml-1" />
                </div>
              </div>
              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">— Music Video · {featured.album}</p>
                <h3 className="font-display wolf-gold-foil text-4xl sm:text-6xl mt-2">{featured.title}</h3>
              </div>
            </button>
          )}
        </div>

        {/* TRILOGY CTA */}
        <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
          <button
            type="button"
            onClick={startTrilogy}
            data-testid="trilogy-watch-btn"
            className="group flex items-center justify-center gap-3 btn-gold-fill text-black font-display tracking-[0.4em] text-sm sm:text-base px-6 sm:px-8 py-4 border border-[#D4AF37] transition-all"
          >
            <span className="text-lg" aria-hidden>🐺</span>
            <span>WATCH THE TRILOGY IN ORDER</span>
            <Play size={16} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <p className="font-editorial italic text-white/55 text-sm sm:text-base">
            All three music videos · back-to-back · uninterrupted.
          </p>
        </div>

        {/* MUSIC VIDEOS — 3 slots */}
        <div className="mt-24">
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] mb-4">— Music Videos</p>
          <h2 className="font-display text-4xl sm:text-6xl text-white mb-10">The Reborn Trilogy</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
            {videos.map((v, i) => (
              <motion.div
                key={v.id}
                data-testid={`video-card-${v.id}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className={`group relative aspect-[4/5] overflow-hidden bg-black border ${
                  v.available ? "border-[#D4AF37]/25 cursor-pointer" : "border-white/10"
                }`}
                onClick={() => v.available && setActiveIdx(i)}
              >
                <img
                  src={v.poster}
                  alt={v.title}
                  className={`absolute inset-0 w-full h-full object-cover transition-all duration-[1400ms] ${
                    v.available
                      ? "opacity-75 group-hover:opacity-95 group-hover:scale-105"
                      : "opacity-30 grayscale"
                  }`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">
                  {v.kind}
                </div>
                <div className="absolute bottom-5 left-5 right-5">
                  <h4 className={`font-display text-2xl sm:text-3xl leading-tight ${v.available ? "text-white" : "text-white/50"}`}>
                    {v.title}
                  </h4>
                  <p className={`font-editorial italic text-sm mt-1 ${v.available ? "text-[#D4AF37]/80" : "text-white/35"}`}>
                    {v.album}
                  </p>
                </div>
                {v.available ? (
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                    <div className="w-16 h-16 rounded-full border border-[#D4AF37]/60 flex items-center justify-center backdrop-blur-sm bg-black/40">
                      <Play size={20} className="text-[#D4AF37] ml-0.5" />
                    </div>
                  </div>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]/80 border border-[#D4AF37]/40 px-4 py-2 bg-black/60 backdrop-blur-sm">
                      Coming soon
                    </span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* IG / TIKTOK */}
        <div className="mt-28 grid lg:grid-cols-2 gap-6">
          <a href="https://instagram.com/wolfmusicau" target="_blank" rel="noreferrer" data-testid="media-instagram-card" className="border border-white/10 bg-[#161616] p-10 lift">
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3">— Instagram</p>
            <h3 className="font-display text-5xl text-white">@wolfmusicau</h3>
            <p className="font-editorial italic text-white/55 mt-4">Atmospheric stills, reels, sessions, ocean.</p>
            <p className="mt-8 text-[11px] uppercase tracking-[0.32em] text-white/70 hover:text-[#D4AF37]">Follow →</p>
          </a>
          <a href="https://tiktok.com/@wolfmusicau" target="_blank" rel="noreferrer" data-testid="media-tiktok-card" className="border border-white/10 bg-[#161616] p-10 lift">
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3">— TikTok</p>
            <h3 className="font-display text-5xl text-white">@wolfmusicau</h3>
            <p className="font-editorial italic text-white/55 mt-4">Reels, snippets, voice memos before the songs become songs.</p>
            <p className="mt-8 text-[11px] uppercase tracking-[0.32em] text-white/70 hover:text-[#D4AF37]">Follow →</p>
          </a>
        </div>
      </div>

      {/* TRILOGY THEATER */}
      {trilogy && (
        <div
          className="fixed inset-0 z-[90] bg-black/97 backdrop-blur-md flex flex-col"
          data-testid="trilogy-theater"
        >
          {/* Theater chrome — chip strip + close */}
          <div className="flex items-center justify-between px-6 sm:px-10 py-5 border-b border-[#D4AF37]/15">
            <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto">
              <span className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] hidden sm:inline">— The Reborn Trilogy</span>
              <span className="text-[#D4AF37]/30 hidden sm:inline">|</span>
              {videos.map((v, i) => {
                const isCurrent = i === trilogy.idx;
                const isDone = i < trilogy.idx;
                return (
                  <span
                    key={v.id}
                    data-testid={`trilogy-chip-${i}`}
                    className={`text-[10px] uppercase tracking-[0.28em] px-3 py-1.5 border whitespace-nowrap transition-colors ${
                      isCurrent
                        ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]"
                        : isDone
                        ? "border-[#D4AF37]/30 text-[#D4AF37]/60"
                        : "border-white/15 text-white/45"
                    }`}
                  >
                    {i + 1}. {v.available ? v.title : "Coming soon"}
                  </span>
                );
              })}
            </div>
            <button
              onClick={closeTrilogy}
              data-testid="trilogy-close-btn"
              className="text-[#D4AF37] hover:text-[#F4C542] ml-4 shrink-0"
              aria-label="Close theater"
            >
              <X size={24} />
            </button>
          </div>

          {/* Stage */}
          <div className="flex-1 flex items-center justify-center px-4 sm:px-10 py-6">
            <div className="relative w-full max-w-6xl aspect-video">
              {trilogy.mode === "video" && videos[trilogy.idx].available ? (
                <VideoPlayer
                  key={videos[trilogy.idx].id}
                  src={videos[trilogy.idx].src}
                  poster={videos[trilogy.idx].poster}
                  autoPlay
                  onEnded={advanceTrilogy}
                />
              ) : trilogy.mode === "interstitial" ? (
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center text-center bg-black border border-[#D4AF37]/25 overflow-hidden"
                  data-testid="trilogy-interstitial"
                >
                  <img
                    src={videos[trilogy.idx].poster}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-25 grayscale"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-black" />
                  <div className="relative z-10 px-8">
                    <p className="text-[10px] uppercase tracking-[0.45em] text-[#D4AF37] mb-6">
                      — Chapter {trilogy.idx + 1} of {videos.length}
                    </p>
                    <h3 className="font-display wolf-gold-foil text-5xl sm:text-7xl leading-[0.9]">
                      Coming Soon
                    </h3>
                    <p className="font-editorial italic text-white/65 text-lg sm:text-2xl mt-6 max-w-xl mx-auto">
                      The next chapter of the Reborn Trilogy drops soon. Join The Pack for early access.
                    </p>
                    <button
                      type="button"
                      onClick={advanceTrilogy}
                      data-testid="trilogy-skip-btn"
                      className="mt-10 text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] border border-[#D4AF37]/40 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 px-5 py-3 inline-flex items-center gap-2"
                    >
                      Skip <Play size={12} />
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center text-center bg-black border border-[#D4AF37]/25"
                  data-testid="trilogy-end"
                >
                  <p className="text-[10px] uppercase tracking-[0.45em] text-[#D4AF37] mb-6">— End of Trilogy</p>
                  <h3 className="font-display wolf-gold-foil text-5xl sm:text-7xl">Still Standing.</h3>
                  <p className="font-editorial italic text-white/70 text-lg sm:text-2xl mt-6 max-w-xl">
                    Built in the dark. Rebuilt in the light.
                  </p>
                  <div className="mt-10 flex flex-wrap gap-3 justify-center">
                    <button
                      type="button"
                      onClick={() => setTrilogy({ idx: 0, mode: "video" })}
                      data-testid="trilogy-replay-btn"
                      className="text-[10px] uppercase tracking-[0.32em] btn-gold-fill text-black border border-[#D4AF37] px-6 py-3 inline-flex items-center gap-2"
                    >
                      <Play size={12} /> Watch Again
                    </button>
                    <button
                      type="button"
                      onClick={closeTrilogy}
                      data-testid="trilogy-finish-btn"
                      className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] border border-[#D4AF37]/40 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 px-6 py-3"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* LIGHTBOX (for grid clicks 1 & 2 — slot 0 plays inline) */}
      {activeIdx !== null && activeIdx > 0 && videos[activeIdx]?.available && (
        <div
          className="fixed inset-0 z-[80] bg-black/95 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setActiveIdx(null)}
          data-testid="video-lightbox"
        >
          <div className="relative w-full max-w-5xl aspect-video" onClick={(e) => e.stopPropagation()}>
            <VideoPlayer
              src={videos[activeIdx].src}
              poster={videos[activeIdx].poster}
              autoPlay
              onClose={() => setActiveIdx(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
